import React from 'react';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: AlertVariant;
  icon?: React.ReactNode;
  onClose?: () => void;
  dismissible?: boolean;
}

const variantStyles: Record<AlertVariant, string> = {
  success: 'bg-green-50 border border-green-200 text-green-800',
  error: 'bg-red-50 border border-red-200 text-red-800',
  warning: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
  info: 'bg-blue-50 border border-blue-200 text-blue-800',
};

const iconVariantStyles: Record<AlertVariant, string> = {
  success: 'text-green-600',
  error: 'text-red-600',
  warning: 'text-yellow-600',
  info: 'text-blue-600',
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      children,
      variant = 'info',
      icon,
      onClose,
      dismissible = false,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={`rounded-lg border p-4 flex items-start gap-3 transition-all duration-200 ${variantStyles[variant]} ${className || ''}`}
        role="alert"
        {...props}
      >
        {icon && (
          <div className={`flex-shrink-0 mt-0.5 ${iconVariantStyles[variant]}`}>
            {icon}
          </div>
        )}

        <div className="flex-1">
          {children}
        </div>

        {dismissible && (
          <button
            onClick={onClose}
            className="flex-shrink-0 text-inherit hover:opacity-70 focus:outline-none"
            aria-label="Close alert"
          >
            <span className="text-lg font-semibold">×</span>
          </button>
        )}
      </div>
    );
  },
);

Alert.displayName = 'Alert';

export default Alert;
